import React, { useState } from 'react';
import { Badge, Button, Group, Pagination, Select, Stack, Table, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { useUsersDirectory } from '../../hooks/useUserApi';
import { AuditFilter, AuditItem, useAudit } from '../../hooks/useRolesApi';
import { formatDateTime } from '../../utils/date';

/** Действия журнала — как `AUDIT_ACTIONS` в `models/AuditEvent.ts` бэкенда. */
const ACTIONS = [
  'role.granted',
  'role.revoked',
  'role.changed',
  'owner.transferred',
  'impersonation.started',
  'reviews.feedback.authors.viewed',
  'quarterly.scores.exported',
  'quarterly.score.removed',
  'competency.revoked',
  'user.email.changed',
  'org.edge.changed',
];

export const AuditTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: people = [] } = useUsersDirectory();
  const [filter, setFilter] = useState<AuditFilter>({ page: 1 });
  const { data } = useAudit(filter);
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'ru-RU';
  const dateFormat = locale === 'en-US' ? 'MMM D, YYYY' : 'D MMM YYYY';
  const hasFilter = !!(filter.action || filter.person || filter.from || filter.to);
  const update = (patch: Partial<AuditFilter>) => setFilter((f) => ({ ...f, ...patch, page: 1 }));

  // Что именно изменилось: роль и её новые чувствительные права, объект ребра руководства.
  const detailOf = (e: AuditItem) => {
    const d = e.details as {
      name?: string;
      objectName?: string;
      op?: string;
      sensitive?: string[];
      isSelf?: boolean;
      selfRights?: boolean;
    };
    return (
      <Group gap={6}>
        {(d.name || d.objectName) && <Text size="sm">{d.name ?? d.objectName}</Text>}
        {d.op && (
          <Text size="sm" c="dimmed">
            {t(`roles.audit.op.${d.op}`)}
          </Text>
        )}
        {d.isSelf && (
          <Badge size="xs" variant="light" color="yellow">
            {t('roles.audit.self')}
          </Badge>
        )}
        {d.selfRights && (
          <Badge size="xs" variant="light" color="yellow">
            {t('roles.audit.selfRights')}
          </Badge>
        )}
        {d.sensitive?.map((k) => (
          <Badge key={k} size="xs" variant="light" color="orange">
            {t(`roles.permissions.${k}`)}
          </Badge>
        ))}
      </Group>
    );
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm" wrap="wrap">
        <Select
          label={t('roles.audit.action')}
          placeholder={t('roles.audit.anyAction')}
          data={ACTIONS.map((a) => ({ value: a, label: t(`roles.audit.actions.${a}`) }))}
          value={filter.action ?? null}
          onChange={(v) => update({ action: v ?? undefined })}
          clearable
          style={{ flex: '1 1 220px' }}
        />
        <Select
          label={t('roles.audit.person')}
          placeholder={t('roles.audit.anyPerson')}
          data={people.map((p) => ({ value: p._id, label: p.name }))}
          value={filter.person ?? null}
          onChange={(v) => update({ person: v ?? undefined })}
          searchable
          clearable
          style={{ flex: '1 1 220px' }}
        />
        <DatePickerInput
          label={t('roles.audit.from')}
          placeholder={t('roles.audit.anyDate')}
          value={filter.from ?? null}
          onChange={(v) => update({ from: v || undefined })}
          maxDate={filter.to}
          valueFormat={dateFormat}
          clearable
          style={{ flex: '1 1 150px' }}
        />
        <DatePickerInput
          label={t('roles.audit.to')}
          placeholder={t('roles.audit.anyDate')}
          value={filter.to ?? null}
          onChange={(v) => update({ to: v || undefined })}
          minDate={filter.from}
          valueFormat={dateFormat}
          clearable
          style={{ flex: '1 1 150px' }}
        />
      </Group>

      <Table.ScrollContainer minWidth={720}>
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={150}>{t('roles.audit.when')}</Table.Th>
              <Table.Th>{t('roles.audit.action')}</Table.Th>
              <Table.Th>{t('roles.audit.actor')}</Table.Th>
              <Table.Th>{t('roles.audit.subject')}</Table.Th>
              <Table.Th>{t('roles.audit.details')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data?.items.map((e) => (
              <Table.Tr key={e._id}>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {formatDateTime(e.createdAt, locale)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{t(`roles.audit.actions.${e.action}`)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{e.actor?.name ?? t('roles.audit.system')}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{e.subject?.name ?? '—'}</Text>
                </Table.Td>
                <Table.Td>{detailOf(e)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data && !data.items.length && (
        <Stack align="center" gap="xs" py="lg">
          <Text size="sm" c="dimmed">
            {hasFilter ? t('roles.audit.emptyFiltered') : t('roles.audit.empty')}
          </Text>
          {hasFilter && (
            <Button variant="light" color="teal" size="xs" onClick={() => setFilter({ page: 1 })}>
              {t('roles.audit.resetFilters')}
            </Button>
          )}
        </Stack>
      )}
      {pages > 1 && (
        <Pagination
          total={pages}
          value={filter.page}
          onChange={(page) => setFilter((f) => ({ ...f, page }))}
          color="teal"
        />
      )}
    </Stack>
  );
};
